import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

import { useState } from "react";
import { getLanguage } from "@/utils/languageUtils";
import {
  labelToLeadStatus,
  leadStatusLabels,
  leadUILabels,
  leadToastLabels,
} from "@/i18n";
import { LeadStatus } from "@/types/enums";

interface StatusDropdownProps {
  initialStatus?: string; // Should be Swedish label (e.g., "Klar", "Kontaktad")
  currentTechnicalStatus?: string; // Current technical enum value (e.g., "CONTACTED", "CONVERTED")
  onStatusChange?: (status: string) => Promise<void>; // Receives technical enum value (e.g., "CONVERTED")
  disabled?: boolean;
}

export const StatusDropdown = ({
  initialStatus,
  currentTechnicalStatus,
  onStatusChange,
  disabled = false,
}: StatusDropdownProps) => {
  const uiLabels = leadUILabels(getLanguage());
  const defaultStatus = initialStatus || uiLabels.selectStatus;

  const [status, setStatus] = useState<string>(defaultStatus);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const handleStatusChange = async (statusLabel: string) => {
    if (disabled || isUpdating) return;

    setIsUpdating(true);

    try {
      // Convert label to technical enum value for backend (uses current language)
      const reverseMap = labelToLeadStatus(getLanguage());
      const technicalStatus = reverseMap[statusLabel];

      if (!technicalStatus) {
        throw new Error(`Invalid status: ${statusLabel}`);
      }
      // Call the async callback if provided and wait for it to complete
      // Pass technical status to the callback
      if (onStatusChange) {
        await onStatusChange(technicalStatus);
      }

      // Only update local state after successful callback (keep label for display)
      setStatus(statusLabel);

      const toastLabels = leadToastLabels(getLanguage());
      toast.success(toastLabels.statusUpdated, {
        description: toastLabels.statusUpdatedDescription.replace(
          "{status}",
          statusLabel,
        ),
      });
    } catch (error) {
      const toastLabels = leadToastLabels(getLanguage());
      toast.error(toastLabels.statusUpdateError, { description: toastLabels.statusUpdateErrorDescription });
    } finally {
      setIsUpdating(false);
    }
  };

  // Get color based on status
  const getStatusColor = () => {
    const labels = leadStatusLabels(getLanguage());
    switch (status) {
      case labels.CONVERTED:
        return "text-green-600";
      case labels.CONTACTED:
        return "text-blue-600";
      case labels.FOLLOW_UP:
        return "text-sky-600";
      case labels.NEW:
        return "text-yellow-600";
      case labels.LOST:
        return "text-red-600";
      default:
        return "";
    }
  };

  const isLoading = disabled || isUpdating;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isLoading}>
        <Button
          variant="outline"
          className={`gap-2 backdrop-blur hover:bg-accent/20 focus:bg-accent/20 ${getStatusColor()}`}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          {isLoading ? uiLabels.updating : status}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card">
        {(() => {
          const statusLabels = leadStatusLabels(getLanguage());
          return (
            <>
              <DropdownMenuItem
                onClick={() => handleStatusChange(statusLabels.NEW)}
                className="text-yellow-600 focus:bg-accent/20"
              >
                {statusLabels.NEW}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange(statusLabels.CONTACTED)}
                className="text-blue-600 focus:bg-accent/20"
              >
                {statusLabels.CONTACTED}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange(statusLabels.FOLLOW_UP)}
                className="text-sky-600 focus:bg-accent/20"
              >
                {statusLabels.FOLLOW_UP}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange(statusLabels.CONVERTED)}
                className="text-green-600 focus:bg-accent/20"
              >
                {statusLabels.CONVERTED}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleStatusChange(statusLabels.LOST)}
                className="text-red-600 focus:bg-accent/20"
              >
                {statusLabels.LOST}
              </DropdownMenuItem>
            </>
          );
        })()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
